import { isNull } from 'lodash';
import { CellValue } from 'models/interfaces/spreadsheet-types/core-types';
import {
  ConvertibleNotesLedgerAfterCellChangedProps,
  ConvertibleNotesLedgerCell,
  ConvertibleNotesLedgerCells
} from '../types';
import * as constants from './constants';
import {
  CONVERSION_PRE_MONEY_ALIAS,
  CONVERSION_PRICE_PER_SHARE_ALIAS,
  CONVERSION_PRICE_PER_SHARE_EXPR,
  PRE_MONEY_SHARE_COUNT_ALIAS,
  VALUE_AT_CONVERSION_ALIAS
} from './constants';

const afterCellChanged = (
  changes: ConvertibleNotesLedgerAfterCellChangedProps[],
  cells: ConvertibleNotesLedgerCells
) => {
  const getExpr = (exr: string, columnLegend: string) => exr.replace(/@/g, columnLegend);

  // addChange is a function that updates the cell as long as it is not already in the changes array
  const addChange = (change: ConvertibleNotesLedgerAfterCellChangedProps) => {
    const existingChange = changes.find(c => c.cell.key === change.cell.key);
    if (!existingChange) {
      changes.push(change);
    } else {
      existingChange.value = change.value;
    }
  };

  const updateCell = ({ cell, isCalculated }: {
    cell: ConvertibleNotesLedgerCell;
    isCalculated: boolean;
  }) => {
    const { exprPair, columnLegend } = cell;
    const cellExprPair = isCalculated ? getExpr(exprPair!, columnLegend) : '';
    addChange({ cell, value: cellExprPair });
  };

  const setFirstTimeConfigCellPairs = (columnLegend: string) => {
    // The default should be that the Next Round PreMoney is shown as editable
    // and the Conversion PreMoney is shown as read-only.
    const nextRoundPremoneyKey = `${columnLegend}${constants.NEXT_ROUND_PREMONEY_ROW}`;
    const nextRoundPremoneyCell = cells[nextRoundPremoneyKey];
    updateCell({ cell: nextRoundPremoneyCell, isCalculated: false });

    const conversionPreMoneyKey = `${columnLegend}${constants.CONVERSION_PRE_MONEY_ROW}`;
    const conversionPreMoneyCell = cells[conversionPreMoneyKey];
    updateCell({ cell: conversionPreMoneyCell, isCalculated: true });

    return changes;
  };

  const isEquityConversionModelPreferredOrCommon = ({ cell, value }: { cell: ConvertibleNotesLedgerCell, value: CellValue }) => {
    const { columnLegend } = cell;
    let equityConversionModelValue = null;
    if (cell.alias === constants.EQUITY_CONVERSION_MODEL_ALIAS) {
      equityConversionModelValue = value;
    } else {
      const equityConversionModelKey = `${columnLegend}${constants.EQUITY_CONVERSION_MODEL_ROW}`;
      const equityConversionModelCell = cells[equityConversionModelKey];
      equityConversionModelValue = equityConversionModelCell.value || '';
    }
    return [constants.PREFERRED_STOCK_VALUE.toString(), constants.COMMON_STOCK_VALUE.toString()].includes(
      equityConversionModelValue?.toString() ?? ''
    );
  };

  const checkIfIsConversionCapValid = ({ cell, value: newValue }: { cell: ConvertibleNotesLedgerCell, value: CellValue }) => {
    const { alias, columnLegend, value: currentValue } = cell;
    const isConversionCapChanging = alias === constants.CONVERSION_CAP_ALIAS;
    if (isConversionCapChanging) {
      return isNull(currentValue) && +newValue! > 0;
    }
    const isEquityConversionModelChanging = alias === constants.EQUITY_CONVERSION_MODEL_ALIAS;
    if (isEquityConversionModelChanging) {
      const isEquityConversionModelChangingFirstTime
        = (isNull(currentValue) || currentValue === constants.LOAN_VALUE) && +newValue! > 0;
      if (isEquityConversionModelChangingFirstTime) {
        const conversionCapKey = `${columnLegend}${constants.CONVERSION_CAP_ROW}`;
        const conversionCapCell = cells[conversionCapKey];
        return +conversionCapCell.value! > 0;
      }
      return false;
    }

    return false;
  };
  let i = 0;
  while (i < changes.length) {
    const change = changes[i];
    i++;
    const { cell } = change;
    const { rowNumberPair, columnLegend, alias } = cell;
    const isValidEquityConversionModel = isEquityConversionModelPreferredOrCommon(change);
    const isConversionCapValid = checkIfIsConversionCapValid(change);

    const getLatestValue = (key: string) => {
      // if the key is in one of the changes, return the value from the change

      const changeCell = changes.find(c => c.cell.key === key);
      if (changeCell) {
        return !!changeCell.value;
      }
      // otherwise, return the value from the cells object
      return cells[key] ? !!cells[key].value : false;
    }

    if ([constants.NEXT_ROUND_PREMONEY_ALIAS, constants.CONVERSION_DISCOUNT_ALIAS, constants.CONVERSION_CAP_ALIAS].includes(alias)
        && getLatestValue(`${columnLegend}${constants.NEXT_ROUND_PREMONEY_ROW}`)
        && getLatestValue(`${columnLegend}${constants.CONVERSION_DISCOUNT_ROW}`)
        && getLatestValue(`${columnLegend}${constants.CONVERSION_CAP_ROW}`)) {
      addChange({ cell: cells[`${columnLegend}${constants.NEXT_ROUND_PREMONEY_ROW}`], value: constants.NEXT_ROUND_PREMONEY_EXPR.replace(/@/g, columnLegend) });
    }

    if ([CONVERSION_PRE_MONEY_ALIAS, PRE_MONEY_SHARE_COUNT_ALIAS].includes(alias)
        && getLatestValue(`${columnLegend}${constants.CONVERSION_PRE_MONEY_ROW}`)
        && getLatestValue(`${columnLegend}${constants.PRE_MONEY_SHARE_COUNT_ROW}`)) {
      addChange({cell: cells[`${columnLegend}${constants.CONVERSION_PRICE_PER_SHARE_ROW}`], value: CONVERSION_PRICE_PER_SHARE_EXPR.replace(/@/g, columnLegend) });
    }

    if ([VALUE_AT_CONVERSION_ALIAS, CONVERSION_PRICE_PER_SHARE_ALIAS].includes(alias)
      && getLatestValue(`${columnLegend}${constants.VALUE_AT_CONVERSION_ROW}`)
      && getLatestValue(`${columnLegend}${constants.CONVERSION_PRICE_PER_SHARE_ROW}`)
    ) {
      addChange({ cell: cells[`${columnLegend}${constants.EXPECTED_SHARES_ROW}`], value: constants.EXPECTED_SHARES_EXPR.replace(/@/g, columnLegend) });
    }

    // If equity conversion model is preferred or common
    if (isValidEquityConversionModel) {
      // If conversion cap is valid and has change by first time
      if (isConversionCapValid) {
        setFirstTimeConfigCellPairs(columnLegend);
      } else if (rowNumberPair) {
        // Update pair cell
        // Update pair cell with expr
        const pairCellKey = `${columnLegend}${rowNumberPair}`;
        const pairCell = cells[pairCellKey];
        updateCell({ cell: pairCell, isCalculated: true });
      }
    }
    if (
      change.cell.alias === constants.EQUITY_CONVERSION_MODEL_ALIAS
      && change.value?.toString() === constants.LOAN_VALUE.toString()
    ) {
      const loanValueKey = `${columnLegend}${constants.LOAN_VALUE_ROW}`;
      const principleAmountKey = `${columnLegend}${constants.NOTE_PRINCIPAL_AMOUNT_ROW}`;
      addChange({ cell: cells[loanValueKey], value: cells[principleAmountKey].value });
    }
  };

  return changes;
};

export default afterCellChanged;
